import type { GenerationModel } from 'src/services/apollo/generated/graphql'
import { ModelVisibilityType } from 'src/services/apollo/generated/graphql'

/**
 * Permission bits for Model roles.
 * These are bitmask values used in `permissionMask`.
 */
export const ModelPermission = {
  /** Can view the model in listings and details */
  VIEW: 1n,
  /** Can use the model for generation */
  GENERATION: 2n,
  /** Can download the model file */
  DOWNLOAD: 4n,
  /** Can edit the model */
  EDIT: 8n,
  /** Can delete the model */
  DELETE: 16n,
} as const

/** Parsed permission flags from permission mask */
export interface ModelPermissionFlags {
  canGenerate: boolean
  // Implement other permissions when needed
}

const DEFAULT_PERMISSIONS = {
  canGenerate: false,
} satisfies ModelPermissionFlags

const DEFAULT_ALLOWED_PERMISSIONS = {
  canGenerate: true,
} satisfies ModelPermissionFlags

/** Converts BigInt permission mask string to boolean flags */
export function parseModelPermissions(
  permissionMask?: string,
): ModelPermissionFlags {
  if (!permissionMask) {
    return DEFAULT_PERMISSIONS
  }

  try {
    const permissionMaskBigInt = BigInt(permissionMask)

    // Enable other permissions when needed
    return {
      // canView: (permissionMaskBigInt & ModelPermission.VIEW) !== 0n,
      canGenerate: (permissionMaskBigInt & ModelPermission.GENERATION) !== 0n,
      // canDownload: (permissionMaskBigInt & ModelPermission.DOWNLOAD) !== 0n,
      // canEdit: (permissionMaskBigInt & ModelPermission.EDIT) !== 0n,
      // canDelete: (permissionMaskBigInt & ModelPermission.DELETE) !== 0n,
    }
  } catch (error) {
    return DEFAULT_PERMISSIONS
  }
}

/** Checks if model is a stub placeholder (authorId === '') for private models user can't view */
export function isModelStub(model: Pick<GenerationModel, 'authorId'>) {
  return model.authorId === ''
}

/** Checks if model requires early access (member-exclusive) */
export function isModelEarlyAccess(
  model?: Pick<GenerationModel, 'accessType'>,
): boolean {
  return getModelAccessType(model) === 'early_access'
}

/** Checks if model is in preview test (role-exclusive) */
export function isModelPreviewTest(
  model?: Pick<GenerationModel, 'accessType'>,
): boolean {
  return getModelAccessType(model) === 'preview'
}

export function getModelAccessType(
  model?: Pick<GenerationModel, 'accessType'>,
) {
  if (model?.accessType) return model.accessType as 'early_access' | 'preview'
  return undefined
}

export function getModelVisibilityType(
  model?: Pick<GenerationModel, 'visibilityType'>,
) {
  return model?.visibilityType as ModelVisibilityType | undefined
}

export function isModelPrivate(
  model?: Pick<GenerationModel, 'visibilityType'>,
) {
  return getModelVisibilityType(model) === ModelVisibilityType.Private
}

function isModelPublic(model?: Pick<GenerationModel, 'visibilityType'>) {
  return getModelVisibilityType(model) === ModelVisibilityType.Public
}

function getMyModelPermission(
  model?: Pick<GenerationModel, 'id' | 'visibilityType' | 'userPermission'>,
) {
  const shouldCheck = !isModelPublic(model)

  // Parse mask to flags; default to allowed if check not needed
  const permissions = shouldCheck
    ? parseModelPermissions(model?.userPermission)
    : DEFAULT_ALLOWED_PERMISSIONS

  return {
    permissions,
  }
}

export function isModelEligibleForGeneration(
  model?: Pick<GenerationModel, 'id' | 'visibilityType' | 'userPermission'>,
) {
  const { permissions } = getMyModelPermission(model)

  return permissions.canGenerate
}
